import { graphScopes } from "./authConfig";

async function getAccessToken(msalInstance, account) {
  try {
    const response = await msalInstance.acquireTokenSilent({
      ...graphScopes,
      account,
    });
    return response.accessToken;
  } catch {
    // Mobile browsers (iOS Safari especially) silently block
    // acquireTokenPopup, leaving the caller hung indefinitely with no
    // error. acquireTokenRedirect always works: it navigates the page to
    // Microsoft, the user signs in for the requested scope, and on return
    // MSAL caches the token so the next acquireTokenSilent call succeeds.
    await msalInstance.acquireTokenRedirect(graphScopes);
    // The line above navigates the page away — control will not return
    // here. Yield a never-resolving promise so callers don't briefly
    // flash an error in the few ms before navigation completes.
    return new Promise(() => {});
  }
}

async function lookupSiteId(token) {
  const response = await fetch(
    "https://graph.microsoft.com/v1.0/sites/sandeu.sharepoint.com:/sites/Sandeu",
    { headers: { Authorization: `Bearer ${token}` } }
  );

  if (!response.ok) {
    const text = await response.text().catch(() => "");
    console.error("Site lookup error:", response.status, text);
    throw new Error(
      `Site lookup failed ${response.status}: ${text || response.statusText}`
    );
  }

  const site = await response.json();
  return site.id;
}

export async function fetchListColumns(msalInstance, account, listName) {
  const token = await getAccessToken(msalInstance, account);
  const siteId = await lookupSiteId(token);

  const url = `https://graph.microsoft.com/v1.0/sites/${siteId}/lists/${listName}/columns`;

  const response = await fetch(url, {
    headers: { Authorization: `Bearer ${token}` },
  });

  if (!response.ok) {
    const text = await response.text().catch(() => "");
    console.error(`Columns fetch error for ${listName}:`, response.status, text);
    throw new Error(`Failed to fetch columns for ${listName}: ${response.status}`);
  }

  const data = await response.json();
  return (data.value || [])
    .filter((col) => !col.readOnly && col.name !== "ContentType" && col.name !== "Attachments")
    .map((col) => ({
      name: col.name,
      displayName: col.displayName,
      type: col.text ? "text" : col.dateTime ? "dateTime" : col.number ? "number" : col.boolean ? "boolean" : col.choice ? "choice" : "other",
      required: col.required || false,
      choices: col.choice?.choices || [],
    }));
}

export async function createListItem(msalInstance, account, listName, fields) {
  const token = await getAccessToken(msalInstance, account);
  const siteId = await lookupSiteId(token);

  const url = `https://graph.microsoft.com/v1.0/sites/${siteId}/lists/${listName}/items`;

  console.log(`POST ${url}`, { fields });

  const response = await fetch(url, {
    method: "POST",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ fields }),
  });

  if (!response.ok) {
    const text = await response.text().catch(() => "");
    console.error(`Create item error for ${listName}:`, response.status, text);
    throw new Error(
      `Failed to create item: ${response.status} ${text || response.statusText}`
    );
  }

  return response.json();
}

async function lookupDriveId(token, siteId) {
  const response = await fetch(
    `https://graph.microsoft.com/v1.0/sites/${siteId}/drives`,
    { headers: { Authorization: `Bearer ${token}` } }
  );

  if (!response.ok) {
    const text = await response.text().catch(() => "");
    console.error("Drive lookup error:", response.status, text);
    throw new Error(`Drive lookup failed: ${response.status}`);
  }

  const data = await response.json();
  const drives = data.value || [];
  console.log("Available drives:", drives.map((d) => ({ id: d.id, name: d.name })));

  const docDrive = drives.find((d) => d.name === "Documents" || d.driveType === "documentLibrary");
  if (!docDrive) {
    throw new Error("No Documents drive found on the site");
  }

  console.log("Using drive:", docDrive.id, docDrive.name);
  return docDrive.id;
}

export async function uploadToHold(msalInstance, account, file) {
  const token = await getAccessToken(msalInstance, account);
  const siteId = await lookupSiteId(token);
  const driveId = await lookupDriveId(token, siteId);

  const now = new Date();
  const datePrefix = now.getFullYear().toString() +
    String(now.getMonth() + 1).padStart(2, "0") +
    String(now.getDate()).padStart(2, "0");
  const holdFilename = `${datePrefix}_HOLD_${file.name}`;

  const url = `https://graph.microsoft.com/v1.0/sites/${siteId}/drives/${driveId}/root:/HOLD/${holdFilename}:/content`;

  console.log(`PUT ${url}`, { size: file.size, type: file.type });

  const response = await fetch(url, {
    method: "PUT",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": file.type || "application/octet-stream",
    },
    body: file,
  });

  if (!response.ok) {
    const text = await response.text().catch(() => "");
    console.error("Upload error:", response.status, text);
    throw new Error(
      `Upload failed: ${response.status} ${text || response.statusText}`
    );
  }

  return { holdFilename, response: await response.json() };
}

// Module 1 — Financial Snapshot
// Reads canonical .xlsx files via the Graph Excel REST API.
// Path: WC/PORT/Canonical/{file}.xlsx — uses default document library (drive/root).

// Excel serial date → JS Date. Excel epoch is 1899-12-30 (1900 leap-year bug).
// Unix epoch lives at Excel serial 25569.
function excelSerialToDate(serial) {
  if (typeof serial !== "number" || !isFinite(serial) || serial <= 0) return null;
  return new Date((serial - 25569) * 86400 * 1000);
}

export async function fetchFinancialSnapshot(msalInstance, account) {
  const token = await getAccessToken(msalInstance, account);
  const siteId = await lookupSiteId(token);

  const filePath = "WC/PORT/Canonical/Entity_BalanceSheet.xlsx";
  const encodedPath = filePath.split("/").map(encodeURIComponent).join("/");
  const url = `https://graph.microsoft.com/v1.0/sites/${siteId}/drive/root:/${encodedPath}:/workbook/worksheets('Entity_BalanceSheet')/usedRange`;

  const response = await fetch(url, {
    headers: { Authorization: `Bearer ${token}` },
  });

  if (!response.ok) {
    const text = await response.text().catch(() => "");
    console.error("Balance sheet fetch error:", response.status, text);
    throw new Error(
      `Failed to read balance sheet: ${response.status} ${text || response.statusText}`
    );
  }

  const data = await response.json();
  const values = data.values || [];

  if (values.length < 2) {
    throw new Error("Balance sheet is empty or missing header row");
  }

  const [header, ...rows] = values;
  const periodCol = header.indexOf("Period");
  const entityCol = header.indexOf("Entity");
  const lineItemCol = header.indexOf("LineItem");
  const amountCol = header.indexOf("Amount_AUD");
  const updatedCol = header.indexOf("LastUpdated");

  if (entityCol === -1 || lineItemCol === -1 || amountCol === -1) {
    throw new Error(
      `Balance sheet missing expected columns. Found: ${header.join(", ")}`
    );
  }

  return rows
    .filter((r) => r[entityCol] && r[lineItemCol])
    .map((r) => ({
      period: r[periodCol] || "",
      entity: r[entityCol],
      lineItem: r[lineItemCol],
      amount: typeof r[amountCol] === "number" ? r[amountCol] : 0,
      lastUpdated: excelSerialToDate(r[updatedCol]),
    }));
}

// Generic workbook discovery — kept for inspecting other canonical files.
export async function discoverWorkbook(msalInstance, account, filePath) {
  const token = await getAccessToken(msalInstance, account);
  const siteId = await lookupSiteId(token);

  const encodedPath = filePath
    .split("/")
    .map(encodeURIComponent)
    .join("/");
  const fileUrlBase = `https://graph.microsoft.com/v1.0/sites/${siteId}/drive/root:/${encodedPath}:/workbook`;

  const sheetsResp = await fetch(`${fileUrlBase}/worksheets`, {
    headers: { Authorization: `Bearer ${token}` },
  });

  if (!sheetsResp.ok) {
    const text = await sheetsResp.text().catch(() => "");
    console.error("Worksheet list error:", sheetsResp.status, text);
    throw new Error(
      `Worksheet list failed ${sheetsResp.status}: ${text || sheetsResp.statusText}`
    );
  }

  const sheetsData = await sheetsResp.json();
  const sheets = sheetsData.value || [];

  const result = { filePath, sheets: [] };

  for (const sheet of sheets) {
    const rangeUrl = `${fileUrlBase}/worksheets('${encodeURIComponent(sheet.name)}')/usedRange`;
    const rangeResp = await fetch(rangeUrl, {
      headers: { Authorization: `Bearer ${token}` },
    });

    if (!rangeResp.ok) {
      const text = await rangeResp.text().catch(() => "");
      console.error(
        `usedRange error for ${sheet.name}:`,
        rangeResp.status,
        text
      );
      result.sheets.push({
        name: sheet.name,
        error: `${rangeResp.status} ${text || rangeResp.statusText}`,
      });
      continue;
    }

    const range = await rangeResp.json();
    result.sheets.push({
      name: sheet.name,
      address: range.address,
      rowCount: range.rowCount,
      columnCount: range.columnCount,
      values: range.values || [],
    });
  }

  return result;
}

export async function fetchListItems(msalInstance, account, listName) {
  const token = await getAccessToken(msalInstance, account);
  const siteId = await lookupSiteId(token);

  const url = `https://graph.microsoft.com/v1.0/sites/${siteId}/lists/${listName}/items?expand=fields&$top=200`;

  const response = await fetch(url, {
    headers: {
      Authorization: `Bearer ${token}`,
      Prefer: "HonorNonIndexedQueriesWarningMayFailRandomly",
    },
  });

  if (!response.ok) {
    const text = await response.text().catch(() => "");
    console.error(`List fetch error for ${listName}:`, response.status, text);
    throw new Error(
      `Graph API error ${response.status}: ${text || response.statusText}`
    );
  }

  const data = await response.json();
  return (data.value || []).map((item) => ({
    id: item.id,
    fields: item.fields || {},
  }));
}

// ExpenseRegister — SharePoint list with 87 items, 17 columns.
// FY columns have encoded internal names: FY24 → _x0046_Y24, etc.
// All other fields use their display names as internal names.
export async function fetchExpenseRegister(msalInstance, account) {
  const token = await getAccessToken(msalInstance, account);
  const siteId = await lookupSiteId(token);

  let url = `https://graph.microsoft.com/v1.0/sites/${siteId}/lists/ExpenseRegister/items?expand=fields&$top=200`;

  const collected = [];
  while (url) {
    const response = await fetch(url, {
      headers: {
        Authorization: `Bearer ${token}`,
        Prefer: "HonorNonIndexedQueriesWarningMayFailRandomly",
      },
    });

    if (!response.ok) {
      const text = await response.text().catch(() => "");
      console.error("ExpenseRegister fetch error:", response.status, text);
      throw new Error(
        `Graph API error ${response.status}: ${text || response.statusText}`
      );
    }

    const data = await response.json();
    for (const item of data.value || []) collected.push(item);
    url = data["@odata.nextLink"] || null;
  }

  return collected.map((item) => {
    const f = item.fields || {};
    return {
      id: item.id,
      expenseName: f.ExpenseName || f.Title || "",
      category: f.Category || "",
      entity: f.Entity || "",
      amount: typeof f.Amount === "number" ? f.Amount : null,
      frequency: f.Frequency || "",
      monthlyEquivalent:
        typeof f.MonthlyEquivalent === "number" ? f.MonthlyEquivalent : null,
      currentAccount: f.CurrentAccount || "",
      historicalAccount: f.HistoricalAccount || "",
      status: f.Status || "",
      cardLinked: f.CardLinked || "",
      notes: f.Notes || "",
      // FY columns have encoded internal names due to SharePoint behaviour.
      fy24: typeof f._x0046_Y24 === "number" ? f._x0046_Y24 : null,
      fy25: typeof f._x0046_Y25 === "number" ? f._x0046_Y25 : null,
      fy26: typeof f._x0046_Y26 === "number" ? f._x0046_Y26 : null,
    };
  });
}

// Inline-edit a single ExpenseRegister item. `fields` uses internal SharePoint
// field names — e.g. { Status: "Migrating" } or { Notes: "switched 5 May" }.
// Returns the updated fields payload from Graph.
export async function patchExpenseRegisterItem(
  msalInstance,
  account,
  itemId,
  fields
) {
  const token = await getAccessToken(msalInstance, account);
  const siteId = await lookupSiteId(token);

  const url = `https://graph.microsoft.com/v1.0/sites/${siteId}/lists/ExpenseRegister/items/${itemId}/fields`;

  const response = await fetch(url, {
    method: "PATCH",
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    body: JSON.stringify(fields),
  });

  if (!response.ok) {
    const text = await response.text().catch(() => "");
    console.error(
      `PATCH ExpenseRegister/${itemId} error:`,
      response.status,
      text
    );
    throw new Error(
      `PATCH failed ${response.status}: ${text || response.statusText}`
    );
  }

  return response.json();
}
